import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Info, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchGeeVeeCategories, fetchGeeVeeCategoryMessages } from '../api/analyticsApi';
import EngagementListModal from './EngagementListModal';

// Clickable horizontal bar list — each category drills into the questions.
const BarList = ({ items, color, onItemClick }) => {
    const max = Math.max(1, ...items.map((i) => i.count || 0));
    if (items.length === 0) {
        return <p className="text-xs text-gray-400 py-4 text-center">No classified questions in this range yet.</p>;
    }
    return (
        <div className="space-y-2.5">
            {items.map((item, idx) => {
                const clickable = !!onItemClick && (item.count || 0) > 0;
                return (
                    <button
                        type="button"
                        key={item.category ?? idx}
                        onClick={clickable ? () => onItemClick(item) : undefined}
                        disabled={!clickable}
                        className={`flex items-center gap-3 w-full text-left rounded-lg px-1 py-0.5 ${
                            clickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                        }`}
                    >
                        <span className="text-xs text-gray-600 w-36 flex-shrink-0 truncate" title={item.label}>
                            {item.label}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                            <div className="h-4 rounded-full"
                                 style={{ width: `${Math.max(6, ((item.count || 0) / max) * 100)}%`, background: color }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-900 w-16 text-right">
                            {(item.count || 0).toLocaleString()}
                            {item.percentage != null && (
                                <span className="text-gray-400 font-normal"> · {item.percentage}%</span>
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

/**
 * "What buyers ask GeeVee about" — question categories assigned nightly by the
 * LLM categorizer. Lives in the GeeVee tab. Click a category to see the
 * underlying questions.
 */
const GeeVeeCategoriesPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0);
    const [listModal, setListModal] = useState({ open: false, loading: false, title: '', subtitle: '', rows: [] });

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchGeeVeeCategories(startDate, endDate)
            .then((res) => { if (myToken === loadTokenRef.current) setData(res); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error('GeeVee categories load failed:', err);
                toast.error('Failed to load GeeVee categories');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    const openCategory = useCallback(async (item) => {
        setListModal({ open: true, loading: true, title: `${item.label} questions`, subtitle: 'GeeVee buyer-portal messages', rows: [] });
        try {
            const res = await fetchGeeVeeCategoryMessages(startDate, endDate, item.category);
            setListModal((m) => ({ ...m, loading: false, rows: res?.rows || [] }));
        } catch (err) {
            console.error('Category drill-down failed:', err);
            toast.error('Failed to load questions');
            setListModal((m) => ({ ...m, loading: false, rows: [] }));
        }
    }, [startDate, endDate]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading question categories…
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#0891b2]" /> GeeVee Question Categories
            </h3>
            <p className="text-xs text-gray-400 mb-4">
                What buyers ask the AI · {data?.classified ?? 0} classified · click a category for the questions
            </p>

            <BarList items={data?.by_category || []} color="#0891b2" onItemClick={openCategory} />

            {(data?.uncategorized ?? 0) > 0 && (
                <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    {data.uncategorized} message{data.uncategorized === 1 ? '' : 's'} awaiting classification (tagged nightly).
                </p>
            )}

            <EngagementListModal
                isOpen={listModal.open}
                onClose={() => setListModal((m) => ({ ...m, open: false }))}
                title={listModal.title}
                subtitle={listModal.subtitle}
                loading={listModal.loading}
                rows={listModal.rows}
                variant="questions"
            />
        </div>
    );
};

export default GeeVeeCategoriesPanel;
